import type { ChangeEventHandler, SubmitEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';

import { PasswordField } from '@/components/common/PasswordField';
import { PasswordStrengthFeedback } from '@/components/common/PasswordStrengthFeedback';
import type { PasswordValidationResponse } from '@/generated/model';

type NewPasswordStepProps = {
  isAccepted: boolean;
  isPending: boolean;
  isPhone: boolean;
  isSubmitting: boolean;
  newPassword: string;
  newPasswordAgain: string;
  validation?: PasswordValidationResponse;
  validationColor: string;
  onNewPasswordChange: ChangeEventHandler<HTMLInputElement>;
  onNewPasswordAgainChange: ChangeEventHandler<HTMLInputElement>;
  onSubmit: SubmitEventHandler<HTMLFormElement>;
};

function getPasswordConfirmationColor(isAccepted: boolean, passwordsMatch: boolean, validationColor: string) {
  if (!isAccepted) {
    return 'text.disabled';
  }
  if (!passwordsMatch) {
    return 'error.main';
  }
  return validationColor;
}

export function NewPasswordStep({
  isAccepted,
  isPending,
  isPhone,
  isSubmitting,
  newPassword,
  newPasswordAgain,
  validation,
  validationColor,
  onNewPasswordChange,
  onNewPasswordAgainChange,
  onSubmit,
}: NewPasswordStepProps) {
  const { t } = useTranslation();
  const passwordsMatch = newPasswordAgain === newPassword;
  const passwordConfirmationColor = getPasswordConfirmationColor(isAccepted, passwordsMatch, validationColor);
  const isPasswordConfirmationValid = newPasswordAgain.length > 0 && passwordsMatch;

  return (
    <Stack sx={{ width: '100%', gap: 1 }} data-qa-element-id="password-change-new-step">
      <Typography variant="body2" color="text.secondary">
        {t('login.passwordChangeV3.warning')}
      </Typography>
      <Stack component="form" onSubmit={onSubmit} noValidate sx={{ gap: 1 }}>
        <PasswordField
          name="newPassword"
          labelKey="login.resetPassword.newPassword"
          autoFocus={!isPhone}
          autoComplete="new-password"
          value={newPassword}
          disabled={isSubmitting}
          onChange={onNewPasswordChange}
        />
        <PasswordStrengthFeedback
          newPassword={newPassword}
          validation={validation}
          isPending={isPending}
          validationColor={validationColor}
        />
        <Stack sx={{ gap: 0.5 }}>
          <PasswordField
            name="newPasswordAgain"
            labelKey="login.resetPassword.passwordAgain"
            autoComplete="new-password"
            value={newPasswordAgain}
            disabled={isSubmitting || !isAccepted}
            onChange={onNewPasswordAgainChange}
          />
          {newPasswordAgain && (
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 0.5 }}
              data-qa-element-id="password-confirmation-status"
            >
              {passwordsMatch ? (
                <CheckCircleOutline sx={{ color: passwordConfirmationColor }} fontSize="small" />
              ) : (
                <CancelOutlined sx={{ color: passwordConfirmationColor }} fontSize="small" />
              )}
              <Typography variant="caption" sx={{ color: passwordConfirmationColor }}>
                {t(
                  passwordsMatch
                    ? 'login.resetPassword.passwordConfirmationMatches'
                    : 'login.resetPassword.passwordConfirmationDoesNotMatch'
                )}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={isSubmitting || !isAccepted || !isPasswordConfirmationValid}
          data-qa-element-id="password-change-save-button"
        >
          {t('login.passwordChangeV3.saveButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
